/* eslint-disable no-promise-executor-return */
/* eslint-disable no-unreachable */
/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import { TOKENS } from '../constants';
import { getCookie, setCookie } from '../utils/cookie';
import {
  getRefreshToken,
  updateTokensAfterRefresh,
  performCompleteLogout,
  isAccessTokenExpired,
  getAuthToken,
} from '../utils/authStorage';

// Flag to prevent multiple simultaneous force relogin attempts
let isForceReloginInProgress = false;

// Flag to prevent multiple simultaneous proactive token refreshes
let isProactiveRefreshInProgress = false;

// Enhanced force relogin handler that immediately stops all requests
const handleForceReloginImmediate = (message) => {
  if (isForceReloginInProgress) {
    return;
  }

  isForceReloginInProgress = true;

  // Perform complete logout
  performCompleteLogout(false);

  // Store message in sessionStorage to show after redirect
  sessionStorage.setItem(
    'force_relogin_message',
    message || 'Session expired. Please log in again.'
  );

  // Force immediate redirect - use replace to prevent back button issues
  window.location.replace('/');
};

/**
 * Proactively refresh access token before it expires
 * Called from request interceptor when token is about to expire
 * @returns {Promise<boolean>} True if refresh successful, false otherwise
 */
const proactivelyRefreshToken = async () => {
  // Prevent multiple simultaneous refresh attempts
  if (isProactiveRefreshInProgress) {
    return false;
  }

  isProactiveRefreshInProgress = true;

  try {
    const refreshToken = getCookie(TOKENS.REFRESH) || getRefreshToken();

    if (!refreshToken) {
      isProactiveRefreshInProgress = false;
      return false;
    }

    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    const data = await response.json();

    if (data.ok && data.access_token) {
      // Update tokens in both localStorage and cookies
      updateTokensAfterRefresh({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        auth_type: data.auth_type,
      });

      // Also update cookies for backward compatibility
      setCookie(TOKENS.ACCESS, data.access_token);
      if (data.refresh_token) {
        setCookie(TOKENS.REFRESH, data.refresh_token);
      }

      isProactiveRefreshInProgress = false;
      return true;
    }

    // Refresh failed
    isProactiveRefreshInProgress = false;
    return false;
  } catch (error) {
    console.error('Proactive token refresh error:', error);
    isProactiveRefreshInProgress = false;
    return false;
  }
};

const defaultHeaders = {};
const initialAccessToken = getCookie(TOKENS.ACCESS) || getAuthToken(TOKENS.ACCESS);
if (initialAccessToken) {
  defaultHeaders.Authorization = `Bearer ${initialAccessToken}`;
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: defaultHeaders,
});

axiosInstance.interceptors.request.use(async (config) => {
  // Allow callers to opt out of auth injection via config.skipAuth
  const skipAuth = Boolean(config && config.skipAuth);
  const hasExplicitAuth = Boolean(config?.headers?.Authorization);
  // If auth is skipped or already provided, proceed without token injection
  if (skipAuth || hasExplicitAuth) {
    return config;
  }

  // Check if access token is expired and refresh if needed
  if (isAccessTokenExpired()) {
    const refreshSuccess = await proactivelyRefreshToken();

    if (!refreshSuccess) {
      // Refresh failed, force logout
      handleForceReloginImmediate('Session expired. Please log in again.');
      return Promise.reject(new Error('Token refresh failed'));
    }
  }

  // Force fresh token retrieval to avoid any caching issues
  const cookieToken = getCookie(TOKENS.ACCESS);
  const localStorageToken = localStorage.getItem(TOKENS.ACCESS);
  const accessToken = cookieToken || localStorageToken;

  const newHeaders = { ...config.headers };

  if (accessToken) {
    newHeaders.Authorization = `Bearer ${accessToken}`;
  } else {
    // Ensure we don't send an Authorization header with an undefined/null token
    delete newHeaders.Authorization;
  }

  return { ...config, headers: newHeaders };
});

// Track ongoing refresh requests to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

// Helper function to check if an error is due to request cancellation/abort
const isRequestCancelled = (error) => {
  return (
    error.name === 'AbortError' ||
    error.code === 'ABORT_ERR' ||
    error.message?.toLowerCase().includes('abort') ||
    error.message?.toLowerCase().includes('cancel') ||
    error.type === 'aborted' ||
    // Network errors that commonly occur during page refresh
    error.message?.toLowerCase().includes('network error') ||
    error.message?.toLowerCase().includes('fetch')
  );
};

// Store refresh attempt timestamp to detect page refresh scenarios
const REFRESH_ATTEMPT_KEY = 'token_refresh_attempt';

const markRefreshAttempt = () => {
  sessionStorage.setItem(REFRESH_ATTEMPT_KEY, Date.now().toString());
};

const clearRefreshAttempt = () => {
  sessionStorage.removeItem(REFRESH_ATTEMPT_KEY);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    // If force relogin is already in progress, reject all requests immediately
    if (isForceReloginInProgress) {
      return Promise.reject(new Error('Force relogin in progress'));
    }

    const originalRequest = error.config;

    // Handle force re-login scenario
    if (error?.response?.status === 401 && error.response.data?.force_relogin) {
      // Prevent multiple simultaneous force relogin attempts
      if (isForceReloginInProgress) {
        return Promise.reject(error.response);
      }

      // If a refresh is in progress, fail all queued requests and reset the flag
      if (isRefreshing) {
        processQueue(new Error('Force relogin'), null);
        isRefreshing = false;
      }

      handleForceReloginImmediate(error.response.data?.message);
      return Promise.reject(error.response);
    }

    // Handle standard unauthorized errors with token refresh
    if (error?.response?.status === 401 && error?.response?.data?.error === 'UNAUTHORIZED') {
      const refreshToken = getRefreshToken() || getCookie(TOKENS.REFRESH);

      if (originalRequest.__isRetryRequest || !refreshToken) {
        // Already retried or no refresh token available
        // If a refresh is in progress (from another request), clean it up
        if (isRefreshing) {
          processQueue(new Error('Force relogin'), null);
          isRefreshing = false;
        }

        handleForceReloginImmediate();
        return Promise.reject(error.response);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest.__isRetryRequest = true;
      isRefreshing = true;

      // Mark that we're attempting a refresh
      markRefreshAttempt();

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
          }),
        });

        const data = await response.json();

        if (data.ok && data.access_token) {
          // Update tokens with new auth_type support
          updateTokensAfterRefresh({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            auth_type: data.auth_type,
          });

          // Also update cookies for backward compatibility
          setCookie(TOKENS.ACCESS, data.access_token);
          if (data.refresh_token) {
            setCookie(TOKENS.REFRESH, data.refresh_token);
          }

          // Update the authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;

          // Process the queue with the new token
          processQueue(null, data.access_token);

          // Clear the refresh attempt marker on success
          clearRefreshAttempt();
          isRefreshing = false;

          // Retry the original request
          return axiosInstance(originalRequest);
        }

        // Token refresh failed - but check if it's a valid failure
        // Clear the refresh attempt marker since we got a response
        clearRefreshAttempt();

        handleForceReloginImmediate();

        processQueue(new Error('Token refresh failed'), null);
        isRefreshing = false;

        return Promise.reject(error.response);
      } catch (refreshError) {
        // eslint-disable-next-line no-console
        console.error('Token refresh error:', refreshError);

        // Check if this is a request cancellation (likely due to page refresh)
        if (isRequestCancelled(refreshError)) {
          // Don't force logout for cancelled requests - let the next request retry
          // Keep the refresh attempt marker so we know a refresh was interrupted

          processQueue(refreshError, null);
          isRefreshing = false;

          return Promise.reject(error.response);
        }

        // For genuine refresh failures, clear the marker and force logout
        clearRefreshAttempt();
        handleForceReloginImmediate();

        processQueue(refreshError, null);
        isRefreshing = false;

        return Promise.reject(error.response);
      }
    }

    // Return the error response data for other types of errors
    return error?.response?.data || error;
  }
);

export default axiosInstance;
